// src/features/analytics/playbackTrend.ts
//
// Pure helpers for the playback trend chart (PlaybackTrendCard). The API omits
// days with no stored data (honest absence); the chart needs a continuous UTC
// day axis, so gaps are zero-filled CLIENT-SIDE only, and only across days that
// were actually measurable — never future days, never days before coverage
// began. Day grain is UTC (the axis is labeled accordingly); day values are
// epoch seconds of the UTC midnight opening the day.

import { DateTime } from 'luxon';
import {
  SECONDS_PER_DAY,
  dayStartSeconds,
  type DayZone,
  type UtcRange,
} from '@/shared/utils/utcRange';
import type { AnalyticsCoverage, PlaybackDailyPoint } from './types';

/**
 * Day-grain x-axis label in the day's own zone: a UTC date for UTC-day
 * series, the local date for a local-day series (dashboard ADR-009) — the
 * point's `day` is the midnight instant opening that day in `zone`, so
 * formatting it in the same zone yields the calendar date the gateway
 * grouped by. Shared by every daily trend chart in the feature.
 */
export function dayLabel(daySeconds: number, zone: DayZone = 'utc'): string {
  return DateTime.fromSeconds(daySeconds, { zone }).toFormat('MMM d');
}

/** UTC-day label (the pre-ADR-009 name, kept for the UTC-only charts). */
export function utcDayLabel(daySeconds: number): string {
  return dayLabel(daySeconds, 'utc');
}

/**
 * Clamp the requested range to the days the chart may honestly zero-fill:
 * start no earlier than coverage.from's UTC day (before it, nothing was
 * collected — zero would be a fabricated measurement) and end no later than
 * the UTC day after `nowSeconds` (future days have no data by definition;
 * presets like currentYear span the whole calendar year).
 */
export function fillableRange(
  range: UtcRange,
  coverage: AnalyticsCoverage | undefined,
  nowSeconds: number,
  zone: DayZone = 'utc'
): UtcRange {
  let from = range.from;
  if (coverage) {
    const coverageDayStart = dayStartSeconds(coverage.from, zone);
    if (coverageDayStart > from) from = coverageDayStart;
  }
  const tomorrow = DateTime.fromSeconds(nowSeconds, { zone }).startOf('day').plus({ days: 1 }).toSeconds();
  const to = Math.min(range.to, tomorrow);
  return { from, to };
}

/**
 * Zero-fill missing calendar days across an end-exclusive day-aligned range
 * so the axis is continuous. Days step by the CALENDAR in `zone` (a local
 * DST day is 23 or 25 hours — never a fixed 86,400 s stride off UTC). Points
 * outside the range pass through untouched at their sorted position
 * (defensive; the API only returns in-range days).
 */
export function fillDailyGaps(
  points: PlaybackDailyPoint[],
  range: UtcRange,
  zone: DayZone = 'utc'
): PlaybackDailyPoint[] {
  const byDay = new Map(points.map((p) => [p.day, p]));
  const out: PlaybackDailyPoint[] = [];
  if (zone === 'utc') {
    for (let day = range.from; day < range.to; day += SECONDS_PER_DAY) {
      out.push(byDay.get(day) ?? { day, sessions: 0, watchTimeMs: 0, uniqueViewers: 0 });
    }
    return out;
  }
  let cursor = DateTime.fromSeconds(range.from, { zone }).startOf('day');
  while (cursor.toSeconds() < range.to) {
    const day = cursor.toSeconds();
    out.push(byDay.get(day) ?? { day, sessions: 0, watchTimeMs: 0, uniqueViewers: 0 });
    cursor = cursor.plus({ days: 1 });
  }
  return out;
}

/**
 * Preset-aware x-tick thinning: show every Nth day label so ~90-day ranges get
 * weekly ticks and year-scale ranges get ~monthly ticks (recharts XAxis
 * `interval` semantics: 0 = every tick, N = skip N between shown ticks).
 */
export function trendTickInterval(days: number): number {
  if (days <= 14) return 0;
  if (days <= 45) return 2;
  if (days <= 120) return 6;
  return 29;
}

// NOTE: no client-side range-total helper on purpose — sessions/uniqueViewers
// are uniq-state merges, so summing per-day points double-counts sessions that
// span UTC midnight (~1.6% high in prod verification). The card's range-total
// annotation uses the overview response's range-wide merge instead.
